import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../lib/auth-context';
import { Lock, Mail, Eye, EyeOff, AlertTriangle, User, X, ArrowRight, Compass, MapPin } from 'lucide-react';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FloatingDot {
  id: number;
  x: number;
  y: number;
  size: number;
  duration: number;
  delay: number;
}

function useFloatingDots(count: number): FloatingDot[] {
  const [dots] = useState<FloatingDot[]>(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 8 + 6,
      delay: Math.random() * 4,
    }))
  );
  return dots;
}

function FloatingParticles() {
  const dots = useFloatingDots(20);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {dots.map((dot) => (
        <motion.div
          key={dot.id}
          className="absolute rounded-full bg-white"
          style={{
            left: `${dot.x}%`,
            top: `${dot.y}%`,
            width: dot.size,
            height: dot.size,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.1, 0.5, 0.1],
          }}
          transition={{
            duration: dot.duration,
            delay: dot.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}

function AuthInput({
  icon: Icon,
  label,
  error: fieldError,
  errorField,
  ...props
}: {
  icon: React.ElementType;
  label: string;
  error?: { field?: string; message?: string } | null;
  errorField?: string;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  const [focused, setFocused] = useState(false);
  const hasError = fieldError?.field === errorField;

  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
        {label}
      </label>
      <div className="relative group">
        <div
          className={`absolute inset-0 rounded-xl transition-all duration-300 -z-10 ${
            focused
              ? 'bg-gradient-to-r from-primary-500/20 to-accent-500/20 blur-sm scale-[1.02]'
              : ''
          }`}
        />
        <Icon
          className={`absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] transition-colors duration-200 ${
            focused ? 'text-primary-500' : 'text-gray-400'
          }`}
        />
        <input
          {...props}
          onFocus={(e) => {
            setFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            props.onBlur?.(e);
          }}
          className={`w-full pl-11 pr-4 py-3.5 text-[15px] bg-white/[0.04] dark:bg-white/[0.04] border rounded-xl transition-all duration-300 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600 outline-none ${
            hasError
              ? 'border-red-400/60 dark:border-red-500/60'
              : focused
                ? 'border-primary-500/40 dark:border-primary-400/40 shadow-[0_0_0_3px_rgba(239,65,54,0.08)]'
                : 'border-gray-200/80 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600'
          } ${props.className || ''}`}
        />
      </div>
    </div>
  );
}

function PasswordInput({
  label,
  error: fieldError,
  errorField,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  error?: { field?: string; message?: string } | null;
  errorField?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
}) {
  const [focused, setFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const hasError = fieldError?.field === errorField;

  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
        {label}
      </label>
      <div className="relative group">
        <div
          className={`absolute inset-0 rounded-xl transition-all duration-300 -z-10 ${
            focused
              ? 'bg-gradient-to-r from-primary-500/20 to-accent-500/20 blur-sm scale-[1.02]'
              : ''
          }`}
        />
        <Lock
          className={`absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] transition-colors duration-200 ${
            focused ? 'text-primary-500' : 'text-gray-400'
          }`}
        />
        <input
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={`w-full pl-11 pr-12 py-3.5 text-[15px] bg-white/[0.04] dark:bg-white/[0.04] border rounded-xl transition-all duration-300 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600 outline-none ${
            hasError
              ? 'border-red-400/60 dark:border-red-500/60'
              : focused
                ? 'border-primary-500/40 dark:border-primary-400/40 shadow-[0_0_0_3px_rgba(239,65,54,0.08)]'
                : 'border-gray-200/80 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600'
          }`}
          placeholder={placeholder}
          required
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          {showPassword ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
        </button>
      </div>
    </div>
  );
}

export function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const [isLogin, setIsLogin] = useState(true);
  const [identifier, setIdentifier] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const { signIn, signUp, error, resetPassword } = useAuth();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (isLogin) {
        await signIn(identifier, password);
      } else {
        if (!acceptTerms) {
          throw { message: "Vous devez accepter les conditions d'utilisation", field: 'terms' };
        }
        await signUp(username, email, password);
      }
      onClose();
    } catch (err) {
      console.error('Auth error:', err);
    }

    setIsSubmitting(false);
  }, [isLogin, identifier, password, acceptTerms, username, email, signIn, signUp, onClose]);

  const handleForgotPassword = useCallback(async () => {
    try {
      if (!resetEmail) return;
      await resetPassword(resetEmail);
      if (!error) setIsResettingPassword(false);
    } catch (err) {
      console.error('Error resetting password:', err);
    }
  }, [resetEmail, resetPassword, error]);

  const formVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
    exit: { opacity: 0, x: -20, transition: { duration: 0.3 } },
  };

  const staggerChildren = {
    visible: {
      transition: { staggerChildren: 0.06 },
    },
  };

  const childVariant = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
  };

  if (!isOpen) return null;

  const renderResetForm = () => (
    <motion.div
      key="reset"
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={formVariants}
      className="space-y-5"
    >
      <div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
          Mot de passe oubli&eacute;
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Entrez votre email pour recevoir un lien de r&eacute;initialisation
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 text-red-600 dark:text-red-400 p-3.5 rounded-xl text-sm flex items-start gap-2.5">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <p>{error.message}</p>
        </div>
      )}

      <AuthInput
        icon={Mail}
        label="Adresse email"
        type="email"
        value={resetEmail}
        onChange={(e) => setResetEmail(e.target.value)}
        placeholder="votre@email.com"
        required
      />

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={() => setIsResettingPassword(false)}
          className="flex-1 px-4 py-3.5 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-all duration-200"
        >
          Retour
        </button>
        <button
          type="button"
          onClick={handleForgotPassword}
          className="flex-1 py-3.5 px-4 bg-primary-500 hover:bg-primary-600 text-white text-sm font-bold rounded-xl transition-all duration-200 shadow-lg shadow-primary-500/25"
        >
          Envoyer
        </button>
      </div>
    </motion.div>
  );

  const renderLoginForm = () => (
    <motion.div
      key="login"
      variants={staggerChildren}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="space-y-4"
    >
      <motion.div variants={childVariant}>
        <AuthInput
          icon={User}
          label="Identifiant"
          type="text"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          placeholder="Nom d'utilisateur ou email"
          error={error}
          errorField="username"
          required
        />
      </motion.div>

      <motion.div variants={childVariant}>
        <PasswordInput
          label="Mot de passe"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Votre mot de passe"
          error={error}
          errorField="password"
        />
        <div className="mt-2.5 text-right">
          <button
            type="button"
            onClick={() => setIsResettingPassword(true)}
            className="text-sm font-medium text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
          >
            Mot de passe oubli&eacute; ?
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  const renderSignUpForm = () => (
    <motion.div
      key="signup"
      variants={staggerChildren}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="space-y-4"
    >
      <motion.div variants={childVariant}>
        <AuthInput
          icon={User}
          label="Nom d'utilisateur"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Choisissez un pseudo"
          error={error}
          errorField="username"
          required
        />
      </motion.div>

      <motion.div variants={childVariant}>
        <AuthInput
          icon={Mail}
          label="Adresse email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="votre@email.com"
          error={error}
          errorField="email"
          required
        />
      </motion.div>

      <motion.div variants={childVariant}>
        <PasswordInput
          label="Mot de passe"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Cr\u00e9ez un mot de passe"
          error={error}
          errorField="password"
        />
      </motion.div>

      <motion.div variants={childVariant} className="flex items-start gap-3 pt-1">
        <div className="relative flex items-center mt-0.5">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className={`h-[18px] w-[18px] text-primary-600 focus:ring-primary-500 rounded border-2 transition-colors cursor-pointer ${
              error?.field === 'terms'
                ? 'border-red-400'
                : 'border-gray-300 dark:border-gray-600'
            }`}
            required
          />
        </div>
        <label className="text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed">
          J'accepte les{' '}
          <a href="https://app.pvtistes.net/cgu" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:text-primary-600 font-medium underline-offset-2 hover:underline">
            conditions d'utilisation
          </a>
          {' '}et la{' '}
          <a href="https://app.pvtistes.net/confidentialite" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:text-primary-600 font-medium underline-offset-2 hover:underline">
            politique de confidentialit&eacute;
          </a>
        </label>
      </motion.div>
    </motion.div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-center justify-center"
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-xl" onClick={onClose} />

          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-[960px] mx-4 sm:mx-6 max-h-[92vh] flex rounded-3xl overflow-hidden shadow-[0_40px_100px_-20px_rgba(0,0,0,0.5)]"
          >
            <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden">
              <div className="absolute inset-0">
                <img
                  src="/images/auth/travel-background.jpg"
                  alt=""
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-gray-900/30" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-gray-900/20" />
              </div>

              <FloatingParticles />

              <div className="relative z-10 flex flex-col justify-between p-10 w-full">
                <div>
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                  >
                    <img
                      src="https://pvtistes.net/wp-content/uploads/2025/01/rgb_pvtistes_logo_negative_white.png"
                      alt="Pvtistes"
                      className="h-8 w-auto"
                    />
                  </motion.div>
                </div>

                <div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.7 }}
                    className="space-y-4"
                  >
                    <div className="flex items-center gap-2 text-white/50 text-xs font-semibold uppercase tracking-[0.2em]">
                      <Compass className="w-3.5 h-3.5" />
                      <span>Communaut&eacute; de voyageurs</span>
                    </div>
                    <h2 className="text-3xl font-bold text-white leading-tight">
                      Votre aventure<br />
                      commence ici.
                    </h2>
                    <p className="text-white/50 text-sm leading-relaxed max-w-[280px]">
                      Rejoignez des milliers de Pvtistes qui pr&eacute;parent et vivent leur exp&eacute;rience &agrave; l'&eacute;tranger.
                    </p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8, duration: 0.6 }}
                    className="mt-8 flex items-center gap-3"
                  >
                    <div className="flex -space-x-2.5">
                      {['CA', 'AU', 'NZ', 'JP'].map((code, i) => (
                        <div
                          key={code}
                          className="w-9 h-9 rounded-full border-2 border-white/20 bg-white/10 backdrop-blur-sm flex items-center justify-center text-xs font-bold text-white/80"
                          style={{ zIndex: 4 - i }}
                        >
                          {code}
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-white/40">
                      <span className="text-white/70 font-semibold">+30</span> destinations PVT
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>

            <div className="flex-1 bg-white dark:bg-gray-900 relative flex flex-col">
              <div className="lg:hidden relative h-32 overflow-hidden flex-shrink-0">
                <img
                  src="/images/auth/travel-background.jpg"
                  alt=""
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-gray-900/40 to-gray-900/80" />
                <FloatingParticles />
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.img
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                    src="https://pvtistes.net/wp-content/uploads/2025/01/rgb_pvtistes_logo_negative_white.png"
                    alt="Pvtistes"
                    className="h-7 w-auto"
                  />
                </div>
              </div>

              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-20 p-2 rounded-full bg-black/10 dark:bg-white/10 hover:bg-black/20 dark:hover:bg-white/20 backdrop-blur-sm transition-all duration-200 group"
              >
                <X className="w-4 h-4 text-white lg:text-gray-500 lg:dark:text-gray-400 group-hover:rotate-90 transition-transform duration-300" />
              </button>

              <div className="flex-1 overflow-y-auto custom-scrollbar">
                <div className="px-6 sm:px-10 py-6 sm:py-8 lg:py-10">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="mb-6 lg:mb-8"
                  >
                    <AnimatePresence mode="wait">
                      {isResettingPassword ? (
                        <motion.div key="reset-title" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-gray-400 dark:text-gray-500 mb-2">
                            <MapPin className="w-3.5 h-3.5" />
                            <span>R&eacute;cup&eacute;ration</span>
                          </div>
                        </motion.div>
                      ) : (
                        <motion.div key="main-title" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-gray-400 dark:text-gray-500 mb-2">
                            <MapPin className="w-3.5 h-3.5" />
                            <span>{isLogin ? 'Bon retour' : 'Bienvenue'}</span>
                          </div>
                          <h2 className="text-2xl sm:text-[28px] font-bold text-gray-900 dark:text-white tracking-tight">
                            {isLogin ? 'Connexion' : 'Inscription'}
                          </h2>
                          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1.5">
                            {isLogin
                              ? 'Acc\u00e9dez \u00e0 votre espace Pvtiste'
                              : 'Cr\u00e9ez votre compte en quelques secondes'}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>

                  {!isResettingPassword && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.4 }}
                      className="mb-6"
                    >
                      <div className="relative flex bg-gray-100 dark:bg-gray-800/80 p-1 rounded-xl">
                        <motion.div
                          className="absolute top-1 bottom-1 rounded-lg bg-white dark:bg-gray-700 shadow-sm"
                          layoutId="tab-indicator"
                          style={{
                            left: isLogin ? '4px' : '50%',
                            right: isLogin ? '50%' : '4px',
                          }}
                          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                        />
                        <button
                          onClick={() => setIsLogin(true)}
                          className={`relative z-10 flex-1 py-2.5 text-sm font-semibold rounded-lg transition-colors duration-200 ${
                            isLogin
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400'
                          }`}
                        >
                          Connexion
                        </button>
                        <button
                          onClick={() => setIsLogin(false)}
                          className={`relative z-10 flex-1 py-2.5 text-sm font-semibold rounded-lg transition-colors duration-200 ${
                            !isLogin
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400'
                          }`}
                        >
                          Inscription
                        </button>
                      </div>
                    </motion.div>
                  )}

                  <AnimatePresence mode="wait">
                    {error && !isResettingPassword && (
                      <motion.div
                        key="error"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mb-5 overflow-hidden"
                      >
                        <div className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 text-red-600 dark:text-red-400 p-3.5 rounded-xl text-sm flex items-start gap-2.5">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <p className="leading-relaxed">{error.message}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <form onSubmit={handleSubmit}>
                    <AnimatePresence mode="wait">
                      {isResettingPassword
                        ? renderResetForm()
                        : isLogin
                          ? renderLoginForm()
                          : renderSignUpForm()}
                    </AnimatePresence>

                    {!isResettingPassword && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.4 }}
                        className="mt-6 space-y-4"
                      >
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="relative w-full py-4 px-6 bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-white dark:text-gray-900 text-sm font-bold rounded-xl transition-all duration-300 shadow-xl shadow-gray-900/10 dark:shadow-white/10 hover:shadow-gray-900/20 dark:hover:shadow-white/20 hover:translate-y-[-1px] active:translate-y-0 overflow-hidden group"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-accent-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                          <span className="relative flex items-center justify-center gap-2.5">
                            {isSubmitting ? (
                              <>
                                <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                                <span>{isLogin ? 'Connexion...' : 'Cr\u00e9ation...'}</span>
                              </>
                            ) : (
                              <>
                                <span className="group-hover:text-white transition-colors duration-500">
                                  {isLogin ? 'Se connecter' : 'Cr\u00e9er mon compte'}
                                </span>
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 group-hover:text-white transition-all duration-300" />
                              </>
                            )}
                          </span>
                        </button>

                        {isLogin && (
                          <p className="text-center text-xs text-gray-400 dark:text-gray-500">
                            Utilisez vos identifiants{' '}
                            <span className="font-semibold text-gray-600 dark:text-gray-300">pvtistes.net</span>
                          </p>
                        )}
                      </motion.div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
